import React from "react";
import _ from "underscore";
import { compose } from "redux";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import MembershipHelpers from "helpers/membership-helpers";
import ActionLogger from "action-logger";
import AnalyticsPreviousLocationContextActions from "actions/analytics-previous-location-context-actions";
import Product from "components/source/new_taxonomy/product";
import ProductDrawer from "components/source/products/product-drawer";
import { loadUserProductRatings } from "actions/product-ratings-actions";
import { displayModal } from "actions/shared-actions";
import { loadShortlists } from "actions/shortlists-actions";
import { refreshGridProductAvailability } from "actions/products-actions";
import { openProductDrawer } from "actions/product-drawer-actions";
import { addUpsell } from "actions/reservations-actions";
import ProductDrawerHelper from "helpers/product-drawer-helper";
import { getDueDate, inMaternityExperience } from "helpers/maternity-helper";
import { analytics, membershipLens, RENTAL_INTENT, flagsAndExperimentNames } from "rtr-constants";
import {
  membershipBagPropType,
  membershipStatePropType,
  gridAndCarouselContextPropType,
  gridContextPropType,
  browserPropType,
  gridViewPropType,
} from "components/propTypes";
import { getUserId, userDataPropType, withUserData } from "components/source/hoc/with-user-data";
import { flagsAndExperimentsPropType, withOnLoadFlagsAndExperiments } from "../../hoc/with-flags-and-experiments";

export class ProductInterfaceComponent extends React.Component {
  static propTypes = {
    availabilityScope: PropTypes.string,
    acquisitionMessaging: PropTypes.object,
    analyticsPreviousLocationContext: gridAndCarouselContextPropType,
    annotationSuffix: PropTypes.string,
    bookedProductPrice: PropTypes.number,
    bookingId: PropTypes.string,
    browser: browserPropType,
    categories: PropTypes.array,
    closeProductDrawer: PropTypes.func,
    component: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
    customHeartLoggingOptions: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    displayedModal: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    errors: PropTypes.object,
    extraPixelData: PropTypes.object,
    favorites: PropTypes.array,
    flagsAndExperiments: flagsAndExperimentsPropType,
    fetchProductsOnApply: PropTypes.bool,
    filters: PropTypes.object,
    gridContext: gridContextPropType,
    gridMessage: PropTypes.object,
    gridType: PropTypes.string,
    gridUrgencyExperimentDisplay: PropTypes.bool,
    gridView: gridViewPropType,
    hideProductInfo: PropTypes.bool,
    isMembershipRentableGrid: PropTypes.bool,
    isBuyNow: PropTypes.bool,
    isPdpCarousel: PropTypes.bool,
    isPostShipmentModal: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    isUnlimitedRentableGrid: PropTypes.bool,
    location: PropTypes.oneOf([
      analytics.ACTION_LOCATIONS.CAROUSEL,
      analytics.ACTION_LOCATIONS.DRAWER,
      analytics.ACTION_LOCATIONS.GRID,
      analytics.ACTION_LOCATIONS.PDP,
    ]),
    locationType: PropTypes.string,
    logMemberNavigatesToPDPNoDrawer: PropTypes.func,
    membershipBag: membershipBagPropType,
    membershipPricing: PropTypes.array,
    membershipState: membershipStatePropType,
    renderMidGridContentBlocks: PropTypes.func,
    mobileWidth: PropTypes.bool,
    noModelImageFirst: PropTypes.bool,
    onClickUpgradePricingTooltip: PropTypes.func,
    page: PropTypes.number,
    productDrawer: PropTypes.shape({
      displayed: PropTypes.string,
      productId: PropTypes.string,
      productIndex: PropTypes.number,
    }),
    products: PropTypes.array.isRequired,
    reviewsCoverPhotos: PropTypes.array,
    saveGridContextToStorage: PropTypes.func,
    setMembershipLens: PropTypes.func,
    shortlists: PropTypes.array,
    subject: PropTypes.object,
    totalPages: PropTypes.number,
    upgradeTooltipText: PropTypes.string,
    userData: userDataPropType,
    workingFilters: PropTypes.object,
    workingSort: PropTypes.string,
    currentIntent: PropTypes.oneOf(Object.values(RENTAL_INTENT)),
    isClearanceProduct: PropTypes.bool,
  };

  componentDidMount() {
    const { userData } = this.props;
    const userId = getUserId(userData);

    if (userId) {
      this.fetchUserProductRatings();
    }
  }

  componentDidUpdate(prevProps) {
    const { favorites, userData } = this.props;

    if (favorites?.length && !prevProps.favorites?.length) {
      this.fetchShortlists();
    }

    const currentUserId = getUserId(userData);
    if (currentUserId) {
      const prevUserId = getUserId(prevProps.userData);
      if (prevUserId !== currentUserId) {
        this.fetchUserProductRatings();
      }
    }
  }

  closeModal = () => {
    this.props.dispatch(displayModal(false));
  };

  fetchUserProductRatings = () => {
    const { dispatch } = this.props;

    dispatch(loadUserProductRatings());

    this.fetchShortlists();
  };

  fetchShortlists = () => {
    const { dispatch, favorites } = this.props;

    dispatch(loadShortlists(favorites));
  };

  handleFetchAvailability = productId => {
    const { dispatch } = this.props;

    dispatch(refreshGridProductAvailability(productId, location));
  };

  openModal = modalName => {
    this.props.dispatch(displayModal(modalName));
  };

  addUpsellToReservation = (sku, reservationId, onSuccessCallback, onFailureCallback, errorObject) => {
    this.props.dispatch(addUpsell(sku, reservationId, onSuccessCallback, onFailureCallback, errorObject));
  };

  openProductDrawer = productInfo => {
    const drawerTypes = {
      unlimited: "unlimitedDrawer",
    };
    let drawerToDisplay;
    if (this.props.isUnlimitedRentableGrid || this.props.isMembershipRentableGrid) {
      drawerToDisplay = drawerTypes.unlimited;
    } else {
      // These are the same grids as in `isProductDrawerEnabled`. If it's not
      // one of them, then let's exit out.
      return;
    }

    const drawerInfo = _.extend(productInfo, {
      displayed: drawerToDisplay,
    });

    this.props.dispatch(openProductDrawer(drawerInfo));

    // If the user clicked on the same product currently shown in the drawer, then do nothing.
    if (this.isDrawerOpen() && this.props.productDrawer.productId === productInfo.productId) {
      return;
    }

    this.logUnlimitedDrawerOpen(productInfo);

    ProductDrawerHelper.loadReviewsForProductDrawer(this.props, productInfo.productId);
  };

  logUnlimitedDrawerOpen = productInfo => {
    if (this.props.isUnlimitedRentableGrid || this.props.isMembershipRentableGrid) {
      ActionLogger.logAction({
        ...this.props.analyticsPreviousLocationContext,
        nth_style: productInfo.productIndex,
        object_type: analytics.OBJECT_TYPE.MEMBERSHIP,
        action: analytics.ACTION_TYPE.DRAWER_OPEN,
        style_name: productInfo.productId,
        num_reviews: productInfo?.product?.reviewSummary?.reviewCount,
        average_rating: productInfo?.product?.reviewSummary?.averageStarRating,
      });
    }
  };

  closeProductDrawer = () => {
    this.props.closeProductDrawer();
  };

  isDrawerOpen = () => {
    return this.props?.productDrawer?.displayed;
  };

  isNonSubInBuyNow = () => {
    const { isBuyNow, userData } = this.props;
    return isBuyNow && !MembershipHelpers.isSubscriptionMember(userData);
  };

  isProductDrawerEnabled = () => {
    // Product Drawer should pop up only on rentable grids with unlimited lens
    const productDrawerDisabled = this.props.flagsAndExperiments?.[flagsAndExperimentNames.RSV_1326_DISABLE_OLD_DRAWER];
    return (
      !productDrawerDisabled &&
      MembershipHelpers.isSubscriptionMember(this.props.userData) &&
      (this.props.isUnlimitedRentableGrid || this.props.isMembershipRentableGrid)
    );
  };

  indexToAddDrawer = (messageIndex = null) => {
    // This is doing some calculations of where to insert the drawer based on the index of the item clicked.
    // The Math.floor helps find the row of indexes to work with.
    // Adding - 1, because of the drawer component evaluates after the product
    let columnCount = 4;

    if (this.props.mobileWidth) {
      columnCount = 2;
    }

    const productIndex = messageIndex ? messageIndex : this.props.productDrawer.productIndex;
    let indexToAddDrawer = columnCount + Math.floor(productIndex / columnCount) * columnCount - 1;

    // This is to fix the index if for some reason there is an unusual number on the last row of the last page of products ( 1 or 2 of a 3 row grid. Or, 1 on a 2 row grid.)
    if (indexToAddDrawer >= this.props.products.length) {
      indexToAddDrawer = this.props.products.length - 1;
    }

    return indexToAddDrawer;
  };

  insertDrawer = index => {
    if (!this.isDrawerOpen()) {
      return null;
    } else if (index === this.indexToAddDrawer()) {
      return this.renderProductDrawer(index);
    }
  };

  renderProductDrawer = () => {
    const { productIndex } = this.props.productDrawer;
    let gridView = {};

    // TODO remove this after an initialStore state is created for gridView.
    if (this.props.gridView) {
      // eslint-disable-next-line prefer-destructuring
      gridView = this.props.gridView;
    }

    return this.renderMembershipProductDrawer(productIndex, gridView);
  };

  renderMidGridContentBlocks = index => {
    if (this.props.renderMidGridContentBlocks) {
      return this.props.renderMidGridContentBlocks(index);
    }
    return null;
  };

  renderMembershipProductDrawer = (productIndex, gridView) => {
    const membershipBag = this.props?.membershipBag ?? {};
    const { membershipState } = this.props;
    const loggingOptionsForDrawer = {
      ...this.props.analyticsPreviousLocationContext,
      ...this.props.gridContext,
      module_type: analytics.MODULE_TYPE.QUICKVIEW,
      object_type: analytics.OBJECT_TYPE.MEMBERSHIP,
      nth_style: productIndex,
    };

    //TODO: Refactor this to use ProductDrawerContainer
    return (
      <ProductDrawer
        key="drawer"
        acquisitionMessaging={this.props.acquisitionMessaging}
        browser={this.props.browser}
        displayedModal={this.props.displayedModal}
        workingFilters={this.props.workingFilters}
        setMembershipLens={this.props.setMembershipLens}
        membershipState={membershipState}
        loggingOptionsForDrawer={loggingOptionsForDrawer}
        membershipBag={membershipBag}
        membershipPricing={this.props.membershipPricing}
        gridView={gridView}
        productDrawer={this.props.productDrawer}
        favorites={this.props.favorites}
        shortlists={this.props.shortlists}
        dispatch={this.props.dispatch}
        closeProductDrawer={this.closeProductDrawer}
        product={this.props.products[productIndex]}
        mobileWidth={this.props.mobileWidth}
        productIndex={productIndex}
        userData={this.props.userData}
        fetchProductsOnApply={this.props.fetchProductsOnApply}
        annotationSuffix={this.props.annotationSuffix}
        upgradeTooltipText={this.props.upgradeTooltipText}
        onClickUpgradePricingTooltip={this.props.onClickUpgradePricingTooltip}
        reviewsCoverPhotos={this.props.reviewsCoverPhotos}
        onFetchAvailability={this.handleFetchAvailability}
        saveGridContextToStorage={this.props.saveGridContextToStorage}
        customHeartLoggingOptions={this.customHeartLoggingOptions(productIndex)}
      />
    );
  };

  customHeartLoggingOptions(index) {
    return {
      ...this.props.analyticsPreviousLocationContext,
      ...this.props.gridContext,
      object_type: analytics.OBJECT_TYPE.GRID,
      nth_style: index,
      ...(this.props.customHeartLoggingOptions || {}),
    };
  }

  productScrollIntoViewLoggingOptions(productIndex) {
    const { analyticsPreviousLocationContext, membershipState, page, products, userData, gridType } = this.props;
    const visibleProducts = products.slice(0, productIndex + 1).map(product => product.id);
    // GN [EXPLANATION] 11/23/20: if the user originated from a carousel we provide the first pixel action.
    // If not we provide the second scrolling action.
    if (!_.isEmpty(analyticsPreviousLocationContext)) {
      return {
        object_type: analytics.ACTION_LOCATIONS.CONTROL_CENTER,
        module_type: analytics.MODULE_TYPE.CAROUSEL_GRID,
        visible_products: JSON.stringify(visibleProducts), // all of the styles shown so far on this grid page
        page_number: page,
        carousel_id: analyticsPreviousLocationContext?.carousel_id,
        nth_carousel: analyticsPreviousLocationContext?.nth_carousel,
        request_id: analyticsPreviousLocationContext?.request_id,
        pick_mode: membershipState?.shipmentAction,
        maternity_due_date: inMaternityExperience(userData) ? getDueDate(userData) : null,
      };
    }

    if (gridType) {
      const { subject, totalPages, workingSort, filters } = this.props;

      return {
        object_type: analytics.OBJECT_TYPE.GRID,
        action: analytics.ACTION_TYPE.VISIBLE_PRODUCTS,
        personalization_location: analytics.OBJECT_TYPE.GRID,
        grid_id: subject.id,
        page: page - 1,
        page_max: totalPages - 1,
        sort_type: workingSort,
        filters: JSON.stringify(filters),
        visible_products: JSON.stringify(visibleProducts),
      };
    }
  }

  logNavigateDirectlyToPDPNoDrawerFromCarousel = () => {
    const { analyticsPreviousLocationContext } = this.props;
    if (!analyticsPreviousLocationContext) {
      return;
    }
    const carouselContextWithModuleType = {
      ...analyticsPreviousLocationContext,
      action: "view_product_details",
      module_type: analytics.MODULE_TYPE.CAROUSEL_GRID,
    };
    ActionLogger.inferAction(carouselContextWithModuleType);

    // NW [EXPLANATION] 4/23/19: Data Science wants to know which carousel the user came from, after they land on the PDP and start hearting/adding to bag.
    // To persist this carouselContext to the PDP, we store it in localStorage. When the PDP mounts, we will save it in Redux and clear it from localStorage, so subsequent navigation is unaffected.
    AnalyticsPreviousLocationContextActions.savePreviousLocationContextToStorage(analyticsPreviousLocationContext);
  };

  logNavigateDirectlyToPDPNoDrawer = productInfo => {
    this.logNavigateDirectlyToPDPNoDrawerFromCarousel();

    const { logMemberNavigatesToPDPNoDrawer } = this.props;

    if (this.isProductDrawerEnabled() && logMemberNavigatesToPDPNoDrawer) {
      logMemberNavigatesToPDPNoDrawer(productInfo);
    }
  };

  getLens = () => {
    const { gridView, userData } = this.props;

    if (gridView?.isClearance || gridView?.isBulk) {
      return null;
    }

    if (MembershipHelpers.isRtrUpdateLens(userData)) return membershipLens.RTRUpdate;
    if (MembershipHelpers.isUnlimitedLens(userData)) return membershipLens.unlimited;
    if (MembershipHelpers.isClassicLens(userData)) return membershipLens.classic;
    return null;
  };

  render() {
    const Component = this.props.component;
    const interfaceFunctions = _.chain(this)
      .functions()
      .map(function (name) {
        return [name, this[name]];
      }, this)
      .object()
      .value();

    return (
      // this is an HOC
      // eslint-disable-next-line react/jsx-props-no-spreading
      <Component {...this.props} {...interfaceFunctions}>
        {_.map(
          this.props.products,
          function (product, i) {
            return [
              <Product
                availabilityScope={this.props.availabilityScope}
                categories={this.props.categories}
                customHeartLoggingOptions={this.customHeartLoggingOptions(i)}
                extraPixelData={this.props.extraPixelData}
                gridUrgencyExperimentDisplay={this.props.gridUrgencyExperimentDisplay}
                key={product.id + "-" + i}
                location={this.props.location}
                logNavigateDirectlyToPDPNoDrawer={this.logNavigateDirectlyToPDPNoDrawer}
                noModelImageFirst={this.props.noModelImageFirst}
                product={product}
                isPostShipmentModal={this.props.isPostShipmentModal}
                scrollIntoViewLoggingOptions={this.productScrollIntoViewLoggingOptions(i)}
                openProductDrawer={this.openProductDrawer}
                productIndex={i}
                isProductDrawerEnabled={this.isProductDrawerEnabled()}
                shouldIncludeProductPrice={true}
                showUnavailable={true}
                hideRatingsActions={product?.hideRatingsActions || this.isNonSubInBuyNow()}
                hideProductInfo={product.hideProductInfo}
                lens={this.getLens()}
                currentIntent={this.props.currentIntent}
                isClearance={this.props.isClearanceProduct || false}
              />,
              this.insertDrawer(i),
              this.renderMidGridContentBlocks(i),
            ];
          },
          this
        )}
      </Component>
    );
  }
}

const mapStateToProps = state => {
  return {
    isBuyNow: state.isBuyNow,
  };
};

export default compose(
  withUserData,
  withOnLoadFlagsAndExperiments(flagsAndExperimentNames.RSV_1326_DISABLE_OLD_DRAWER),
  connect(mapStateToProps, null)
)(ProductInterfaceComponent);
